
import { yupResolver } from '@hookform/resolvers/yup';
import { LoadingButton } from '@mui/lab';
import { Box, Grid, Typography } from '@mui/material';
import React, { useEffect } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { useRecoilValue } from 'recoil';
import { FileManagementService } from '../../../../../Component/Api/FileManagement/FileManagementService';
import { DataTable } from '../../../../../Component/Common/Components/DataTable/DataTable';
import AppFileDropzone from '../../../../../Component/Common/Components/FormValidationInputs/AppFileDropZone';
import AppTextInput from '../../../../../Component/Common/Components/FormValidationInputs/AppTextInput';
import { ColumnSortOption } from '../../../../../Component/Model/Dto/ColumnSortOption';
import { FileManagementUploadSearchDto, FileManagementSearchInput } from '../../../../../Component/Model/Dto/FileManagement';
import { EntriesPerPage } from '../../../../../Component/Model/Table/Table';
import { isNullOrEmpty } from '../../../../../Component/Model/helpers';
import { UserAtom } from '../../../../../State/user.Atom';
import { FileManagmentUploadTableMapper } from './FileManagementUploadTableMapper';
import { FileManagementUploadValidationSchema } from './FileManagementUploadValidationSchema';
import { toast } from 'react-toastify';
import { PortalDocumentService } from '../../../../../Component/Api/PortalDocument/PortalDocumentUploadService';
import { SmallLoading } from '../../../../../Component/Common/Components/Loading/IntermediateLoading';
import { NoDataScreen } from '../../../../../Component/Common/Components/NoDataScreen/NoDataScreen';

interface IProps {
    scorecardId: string;
}

export const FileManagementUpload = (props: IProps) => {

    const user = useRecoilValue(UserAtom);

    const [filteredItemsCount, setFilteredItemsCount] = React.useState<number>(0);
    const [totalItemsCount, setTotalItemsCount] = React.useState<number>(0);
    const [entriesPerPage, setEntriesPerPage] = React.useState<EntriesPerPage>({ defaultValue: 5 });
    const [pageNumber, setPageNumber] = React.useState<number>(1);
    const [isLoading, setIsLoading] = React.useState(false);
    const [searchValue, setSearchValue] = React.useState<string>("");
    const [columnSortOptions, setColumnSortOptions] = React.useState<ColumnSortOption[]>([]);
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const [data, setData] = React.useState<FileManagementUploadSearchDto[]>([]);
    const [counter, setCounter] = React.useState(0);

    const searchInput: FileManagementSearchInput = {
        scorecardId: props.scorecardId,
        pageNumber: pageNumber,
        pageSize: entriesPerPage.defaultValue!,
        searchValue: searchValue,
        columnSortOptions: columnSortOptions
    }

    const { control, handleSubmit, setValue } = useForm({
        mode: 'all',
        resolver: yupResolver(FileManagementUploadValidationSchema)
    });

    const downloadFile = async (id: number) => {
        try {
            const response = await PortalDocumentService.GetById(id);
            window.open(response.data);
        } catch (error) {
            console.log(error);
        }
    }

    const entriesPerPageChanged = (entriesPerPage: number) => {
        setEntriesPerPage({ defaultValue: entriesPerPage });
        setPageNumber(1);
    }

    const pageNumberChanged = (pageNumber: number) => {
        setPageNumber(pageNumber);
    }

    const searchValueChanged = (value: string) => {
        setSearchValue(value);
        setPageNumber(1);
    }

    const columnSortingChanged = (sortOption: ColumnSortOption) => {
        setColumnSortOptions([sortOption]);
        setPageNumber(1);
    }

    const fetchData = async () => {
        setIsLoading(true);
        try {
            const response = await FileManagementService.UploadSearch(searchInput);
            setTotalItemsCount(response.data.totalItemsCount);
            setFilteredItemsCount(response.data.filteredItemsCount);
            setData(response.data.results);
        } catch (error) {
            console.log(error);
        } finally {
            setIsLoading(false);
            setCounter(counter + 1);
        }
    }


    useEffect(() => {
        if (!isNullOrEmpty(props.scorecardId)) {
            fetchData();
        }
    }, [props.scorecardId, searchValue, entriesPerPage, pageNumber, columnSortOptions]);

    const handleDataSubmit = async (data: FieldValues) => {
        setIsSubmitting(true);
        try {
            const comment = data.comment;
            const file = data.file;
            const headers = { 'Content-Type': 'multipart/form-data' };
            const documentTypeId = 195;
            const link = 'FileManagementUpload';
            let formData = new FormData();

            if (comment) {
                formData.append('Comment', comment);
            }

            formData.append('ScorecardId', props.scorecardId);
            formData.append('File', file, file.name);
            formData.append('DocumentTypeId', documentTypeId.toString());
            formData.append('Link', link);

            await FileManagementService.UploadDocument(formData, headers);
            toast.success("File successfully uploaded");
            setValue('file', null);
            setValue('comment', null);
            fetchData();
        } catch (error) {
            console.log(error);
        }
        finally {
            setIsSubmitting(false);
        }
    }

    const tableData = FileManagmentUploadTableMapper(data, downloadFile);

    if (!user.hasAuditRole) {
        return (
            <>
                <Box style={{ paddingTop: '10px' }}>
                    <Typography variant='h5' color='secondary'>
                        Files already uploaded
                    </Typography>
                    <Typography variant='body2' color='secondary'>
                        Here's a list of files that have already been uploaded by your organisation. You can download any files you've uploaded for reference, but deleting files isn't allowed.
                        Please upload the latest version of your file with a comment to let your VA know which version to use.
                    </Typography>
                </Box>

                <DataTable
                    key={totalItemsCount}
                    table={tableData!}
                    entriesPerPage={entriesPerPage}
                    pageNumber={pageNumber}
                    filteredItemsCount={filteredItemsCount}
                    totalItemsCount={totalItemsCount}
                    entriesPerPageChanged={entriesPerPageChanged}
                    pageNumberChanged={pageNumberChanged}
                    columnSortingChanged={columnSortingChanged}
                    searchValueChanged={searchValueChanged}
                    isLoading={isLoading}
                    manualSorting={true}
                    canSearch={true}
                />

                <Box style={{ paddingTop: '20px' }} component="form" id={"FileManagementUploadForm"} onSubmit={handleSubmit(handleDataSubmit)}>
                    <Typography variant='h5' color='secondary'>
                        Upload a file
                    </Typography>
                    <Typography variant='body2' color='secondary'>
                        Upload your populated file. After uploading your file, your VA will download all files containing claims within your organisation to collate into a single file to be imported
                        and processed in the system. Your VA will let you know when they are done so that you can review and submit your claims for verification.
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={6}>
                            <Box mt={2} display='flex' justifyContent='space-between' alignItems='center'>
                                <AppFileDropzone control={control} name="file" width="100%" />
                            </Box>
                        </Grid>

                        <Grid item xs={6}>
                            <Box display="flex" flexDirection="column" justifyContent="space-between" height="100%">
                                <Box mt={"16px"}>
                                    <AppTextInput control={control} name="comment" label='Capture comments for your VA (optional)' multiline rows={5} />
                                </Box>
                                <Box>
                                    <LoadingButton variant='contained' loading={isSubmitting} form={"FileManagementUploadForm"} type="submit">Save & Upload</LoadingButton>
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>
                </Box>

            </>
        )
    } else {
        return (
            <>
                {counter === 0 && <SmallLoading />}
                {counter > 0 && totalItemsCount > 0 &&
                    <>
                        <Box style={{ paddingTop: '10px' }}>
                            <Typography variant='body2' color='secondary'>
                                The following files have been uploaded onto the system by your client.
                            </Typography>
                        </Box>
                        <DataTable
                            key={totalItemsCount}
                            table={tableData!}
                            entriesPerPage={entriesPerPage}
                            pageNumber={pageNumber}
                            filteredItemsCount={filteredItemsCount}
                            totalItemsCount={totalItemsCount}
                            entriesPerPageChanged={entriesPerPageChanged}
                            pageNumberChanged={pageNumberChanged}
                            columnSortingChanged={columnSortingChanged}
                            searchValueChanged={searchValueChanged}
                            isLoading={isLoading}
                            manualSorting={true}
                            canSearch={true}
                        />
                    </>
                }
                {counter > 0 && totalItemsCount === 0 &&
                    <NoDataScreen subText1='No files have been uploaded by your client yet.' />
                }
            </>
        )
    }
}
